/**
 * Javascript Error Logger
 * GOAL : Make browser errors accessible on mobile devices.
 * Captures console errors, runtime errors, and unhandled rejections.
 * Persists to localStorage with debounced flush + auto-pruning.
 * Also supports live subscriptions and DOM mirroring of new entries.
 *
 * Usage:
 *   import initJsErrorLogger from "./error-logger.js";
 *   const logger = initJsErrorLogger();
 *   logger.attachTarget('#js-errors-stream', { replayExisting: true });
 */

export default function initJsErrorLogger(userOptions = {}) {
  // Skip on non-browser/SSR
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return shimApi();
  }

  const options = {
    storageKey: "app:errorLogs",
    maxEntries: 200,
    capture: { error: true, warn: false, log: false },
    exposeGlobal: true,           // window.JsErrorLog API
    debounceMs: 250,
    ...userOptions,
  };

  // --- state ---
  const STORAGE_KEY = String(options.storageKey);
  const MAX_ENTRIES = Number(options.maxEntries) || 200;
  const CAPTURE = { ...{ error: true, warn: false, log: false }, ...options.capture };
  const original = {};
  let buffer = readLogs();
  let pendingFlush = null;

  // simple subscriber registry
  const subscribers = new Set();
  const notify = (entry) => {
    // callbacks
    subscribers.forEach(fn => {
      try { fn(entry); } catch { /* never block */ }
    });
    // DOM event for anyone who prefers CustomEvent
    try {
      window.dispatchEvent(new CustomEvent("errorlog:entry", { detail: entry }));
    } catch { /* IE11/old browsers can polyfill if needed */ }
  };

  // --- utils ---
  function nowISO() { return new Date().toISOString(); }

  function safeStringify(val) {
    if (val instanceof Error) return `${val.name}: ${val.message}`;
    if (typeof val === "string") return val;
    try {
      const seen = new WeakSet();
      return JSON.stringify(val, (k, v) => {
        if (v instanceof Error) return { name: v.name, message: v.message, stack: v.stack };
        if (v && typeof v === "object") {
          if (seen.has(v)) return "[Circular]";
          seen.add(v);
        }
        return v;
      });
    } catch {
      try { return String(val); } catch { return "[Unstringifiable]"; }
    }
  }

  function readLogs() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return (raw && JSON.parse(raw)) || [];
    } catch {
      return [];
    }
  }

  function scheduleFlush() {
    if (pendingFlush) return;
    pendingFlush = setTimeout(flush, options.debounceMs);
  }

  function flush() {
    pendingFlush && clearTimeout(pendingFlush);
    pendingFlush = null;

    if (buffer.length > MAX_ENTRIES) buffer = buffer.slice(-MAX_ENTRIES);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
    } catch {
      // Drop older half until it fits
      let start = Math.floor(buffer.length / 2);
      while (start > 0) {
        try {
          const sliced = buffer.slice(start);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(sliced));
          buffer = sliced;
          break;
        } catch {
          start = Math.floor(start / 2);
        }
      }
    }
  }

  function push(entry) {
    buffer.push(entry);
    scheduleFlush();
    notify(entry); // tell listeners about the new entry
  }

  // --- event capture ---
  function recordConsole(type, args) {
    try {
      const errObj = args.find(a => a instanceof Error);
      push({
        ts: nowISO(),
        type: `console.${type}`,
        message: args.map(safeStringify).join(" "),
        stack: errObj && (errObj.stack || `${errObj.name}: ${errObj.message}`),
        url: location.href,
        ua: navigator.userAgent,
      });
    } catch { /* never block */ }
  }

  function wrapConsole(method) {
    if (!console[method]) return;
    original[method] = console[method].bind(console);
    console[method] = (...args) => {
      recordConsole(method, args);
      original[method](...args);
    };
  }

  if (CAPTURE.error) wrapConsole("error");
  if (CAPTURE.warn) wrapConsole("warn");
  if (CAPTURE.log) wrapConsole("log");

  // runtime errors
  const onError = (ev) => {
    try {
      push({
        ts: nowISO(),
        type: "window.onerror",
        message: ev.message,
        stack: ev.error && ev.error.stack,
        url: ev.filename || location.href,
        line: ev.lineno,
        col: ev.colno,
        ua: navigator.userAgent,
      });
    } catch { }
  };
  window.addEventListener("error", onError);

  // unhandled promise rejections
  const onRejection = (ev) => {
    try {
      const r = ev.reason;
      push({
        ts: nowISO(),
        type: "unhandledrejection",
        message: r ? (r.message || String(r)) : "Unhandled rejection",
        stack: r && r.stack,
        url: location.href,
        ua: navigator.userAgent,
      });
    } catch { }
  };
  window.addEventListener("unhandledrejection", onRejection);

  // --- API ---
  const api = {
    get: () => readLogs(),
    clear: () => { localStorage.removeItem(STORAGE_KEY); buffer = []; },
    flush,
    dumpToConsole: () =>
      (original.log ? original.log : console.log)("[JsErrorLog]", readLogs()),
    restore: () => {
      ["error", "warn", "log"].forEach(m => {
        if (original[m]) console[m] = original[m];
      });
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    },

    // subscribe/unsubscribe
    onEntry(fn) { subscribers.add(fn); return () => subscribers.delete(fn); },

    // Mirror into a DOM node
    attachTarget(targetOrSelector, opts = {}) {
      const {
        limit = 200,                   // max DOM nodes to keep
        replayExisting = false,        // render current logs immediately
        insert = "append",             // "append" (bottom) or "prepend" (top)
        formatter = defaultFormatter,  // (entry) => string
        nodeTag = "pre",               // tag used for each line
        className = "errorlog-entry",  // class on each node
        emptyText = "No log",          // placeholder if no logs
        emptyClass = "errorlog-empty", // class for placeholder
      } = opts;

      const el = resolveElement(targetOrSelector);

      if (!el) {
        console.log('JsErrorLog target not found', targetOrSelector);
        return () => { };
      }

      // --- placeholder ---
      function ensureEmptyPlaceholder() {
        if (!el.firstChild) {
          const placeholder = document.createElement(nodeTag);
          placeholder.className = emptyClass;
          placeholder.textContent = emptyText;
          el.appendChild(placeholder);
        }
      }
      function removeEmptyPlaceholder() {
        const placeholder = el.querySelector(`.${emptyClass}`);
        if (placeholder) placeholder.remove();
      }

      const render = (entry) => {
        removeEmptyPlaceholder();
        const line = document.createElement(nodeTag);
        line.className = className;
        line.textContent = formatter(entry);
        if (insert === "prepend" && el.firstChild) {
          el.insertBefore(line, el.firstChild);
        } else {
          el.appendChild(line);
        }
        // enforce limit
        while (el.childElementCount > limit) {
          insert === "prepend"
            ? el.removeChild(el.lastElementChild)
            : el.removeChild(el.firstElementChild);
        }
      };

      // initial state
      if (replayExisting) {
        const logs = this.get();
        if (logs.length) logs.forEach(render);
        else ensureEmptyPlaceholder();
      } else {
        ensureEmptyPlaceholder();
      }

      const off = this.onEntry(render);
      return () => off(); // detach function
    }

  };

  if (options.exposeGlobal) {
    Object.defineProperty(window, "JsErrorLog", { value: api, configurable: true });
  }

  return api;

  // --- helpers ---
  function resolveElement(target) {
    if (!target) return null;
    if (typeof target === "string") return document.querySelector(target);
    if (target instanceof Element) return target;
    return null;
  }

  function defaultFormatter(e) {
    const t = new Date(e.ts);
    const time = isNaN(t) ? e.ts : t.toLocaleTimeString();
    const where = e.url ? ` @ ${e.url}` : "";
    const pos = (e.line || e.col) ? `:${e.line ?? "?"}:${e.col ?? "?"}` : "";
    return `[${time}] ${e.type}${pos} — ${e.message}${where}`;
  }
}

// Minimal API for SSR/tests
function shimApi() {
  const noop = () => { };
  return {
    get: () => [],
    clear: noop,
    dumpToConsole: noop,
    restore: noop,
    flush: noop,
    onEntry: () => noop,
    attachTarget: () => noop,
  };
}
